import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ShieldIcon from '@mui/icons-material/Shield';

import { adminGetIntelUsers } from '../../../api/Api';
import { useColors } from '../../../theme/ColorModeContext';
import type { IntelUserRow, IntelUsers as Users, LifecycleStage, Recency } from './types';
import { CHANNEL_LABELS, LEARNING_LABELS, RECENCY_LABELS, STAGE_LABELS, fmtDate, fmtDaysAgo } from './format';
import { MONO, RecencyChip, Section, StageChip, TrackingNote, useTableSx } from './widgets';
import { SortHeader, sortRows, useSort } from './table';

interface Props {
    token: string;
    includeAdmins: boolean;
}

const PAGE = 100;

const CSV_COLUMNS: (keyof IntelUserRow)[] = [
    'username', 'email', 'created_at', 'confirmed', 'signup_channel', 'lifecycle_stage', 'recency',
    'last_active_at', 'days_since_active', 'active_days', 'active_days_30', 'logins', 'challenge_solves',
    'stage_solves', 'points', 'quiz_attempts', 'primary_track', 'first_action', 'learning_mix', 'marketing_consent',
];

/** CSV of the rows on screen. Cells that a spreadsheet would run as a formula
 *  are prefixed with a quote. */
export const toCsv = (rows: IntelUserRow[]): string => {
    const cell = (value: unknown) => {
        let text = value == null ? '' : Array.isArray(value) ? value.join(' ') : String(value);
        if (/^[=+\-@]/.test(text)) text = `'${text}`;
        return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    return [CSV_COLUMNS.join(','), ...rows.map((row) => CSV_COLUMNS.map((column) => cell(row[column])).join(','))].join('\n');
};

const IntelUsers = ({ token, includeAdmins }: Props) => {
    const colors = useColors();
    const table = useTableSx();
    const navigate = useNavigate();
    const [data, setData] = useState<Users | null>(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [stage, setStage] = useState<LifecycleStage | 'all'>('all');
    const [recency, setRecency] = useState<Recency | 'all'>('all');
    const [channel, setChannel] = useState('all');
    const [limit, setLimit] = useState(PAGE);
    const sort = useSort<IntelUserRow>('created_at');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        adminGetIntelUsers(token, includeAdmins).then((result) => {
            if (cancelled) return;
            setData(result);
            setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [token, includeAdmins]);

    const channels = useMemo(() => Array.from(new Set((data?.users || []).map((row) => row.signup_channel))).sort(), [data]);

    const filtered = useMemo(() => {
        const needle = search.trim().toLowerCase();
        const rows = (data?.users || []).filter((row) =>
            (!needle || row.username.toLowerCase().includes(needle) || row.email.toLowerCase().includes(needle))
            && (stage === 'all' || row.lifecycle_stage === stage)
            && (recency === 'all' || row.recency === recency)
            && (channel === 'all' || row.signup_channel === channel));
        return sortRows(rows, sort.key, sort.dir);
    }, [data, search, stage, recency, channel, sort.key, sort.dir]);

    if (loading && !data) {
        return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress sx={{ color: colors.accent.cyan }} /></Box>;
    }
    if (!data) {
        return <Typography sx={{ color: colors.text.muted }}>Could not load users.</Typography>;
    }

    const exportCsv = () => {
        const blob = new Blob([toCsv(filtered)], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `web3pwn-users-${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const selectSx = { fontFamily: MONO, fontSize: '0.78rem', minWidth: 150 };

    return (
        <Box sx={{ opacity: loading ? 0.6 : 1 }}>
            <TrackingNote since={data.tracking_since} />
            <Section
                title={`Users · ${filtered.length} of ${data.users.length}`}
                caption="Click a user for their full journey. Filter by segment to build a target list, e.g. Activated + Dormant for re-engagement, or Signed up only for onboarding follow-up."
                action={<Button size="small" variant="outlined" onClick={exportCsv} disabled={!filtered.length} sx={{ fontFamily: MONO, textTransform: 'none' }}>Export CSV</Button>}
            >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mb: 2 }}>
                    <TextField size="small" placeholder="Search username or email" value={search} onChange={(event) => setSearch(event.target.value)} sx={{ minWidth: 240, '& input': { fontFamily: MONO, fontSize: '0.8rem' } }} />
                    <Select size="small" value={stage} onChange={(event) => setStage(event.target.value as LifecycleStage | 'all')} sx={selectSx}>
                        <MenuItem value="all">Any stage</MenuItem>
                        {(Object.keys(STAGE_LABELS) as LifecycleStage[]).map((key) => <MenuItem key={key} value={key}>{STAGE_LABELS[key]}</MenuItem>)}
                    </Select>
                    <Select size="small" value={recency} onChange={(event) => setRecency(event.target.value as Recency | 'all')} sx={selectSx}>
                        <MenuItem value="all">Any recency</MenuItem>
                        {(Object.keys(RECENCY_LABELS) as Recency[]).map((key) => <MenuItem key={key} value={key}>{RECENCY_LABELS[key]}</MenuItem>)}
                    </Select>
                    <Select size="small" value={channel} onChange={(event) => setChannel(String(event.target.value))} sx={selectSx}>
                        <MenuItem value="all">Any channel</MenuItem>
                        {channels.map((key) => <MenuItem key={key} value={key}>{CHANNEL_LABELS[key] || key}</MenuItem>)}
                    </Select>
                </Box>

                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={table.head}>
                                <SortHeader label="User" field="username" sort={sort} align="left" />
                                <SortHeader label="Joined" field="created_at" sort={sort} align="left" />
                                <SortHeader label="Channel" field="signup_channel" sort={sort} align="left" />
                                <TableCell>Stage</TableCell>
                                <TableCell>Recency</TableCell>
                                <SortHeader label="Last active" field="days_since_active" sort={sort} />
                                <SortHeader label="Active days" field="active_days" sort={sort} title="Total (last 30 days)" />
                                <SortHeader label="Logins" field="logins" sort={sort} />
                                <SortHeader label="Challenges" field="challenge_solves" sort={sort} />
                                <SortHeader label="Theory" field="stage_solves" sort={sort} />
                                <SortHeader label="Points" field="points" sort={sort} />
                                <TableCell>Main track</TableCell>
                                <TableCell>Learns via</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.slice(0, limit).map((row) => (
                                <TableRow key={row.id} hover onClick={() => navigate(`/admin/intel/user/${row.id}`)} sx={{ ...table.row, cursor: 'pointer' }}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            {row.admin && <ShieldIcon sx={{ color: colors.accent.purple, fontSize: '0.9rem' }} />}
                                            <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: colors.text.primary }}>{row.username}</Typography>
                                        </Box>
                                        <Typography sx={{ fontFamily: MONO, fontSize: '0.66rem', color: colors.text.muted }}>{row.email}</Typography>
                                    </TableCell>
                                    <TableCell sx={table.mono}>{fmtDate(row.created_at)}</TableCell>
                                    <TableCell sx={table.mono}>{CHANNEL_LABELS[row.signup_channel] || row.signup_channel}</TableCell>
                                    <TableCell><StageChip stage={row.lifecycle_stage} /></TableCell>
                                    <TableCell><RecencyChip recency={row.recency} /></TableCell>
                                    <TableCell align="right" sx={table.mono}>{fmtDaysAgo(row.days_since_active)}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{row.active_days} <Box component="span" sx={{ color: colors.text.muted }}>({row.active_days_30})</Box></TableCell>
                                    <TableCell align="right" sx={table.mono}>{row.logins}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{row.challenge_solves}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{row.stage_solves}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{row.points}</TableCell>
                                    <TableCell sx={table.mono}>{row.primary_track || '-'}</TableCell>
                                    <TableCell sx={table.mono}>{LEARNING_LABELS[row.learning_mix] || row.learning_mix}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {filtered.length > limit && (
                    <Box sx={{ textAlign: 'center', pt: 2 }}>
                        <Button size="small" onClick={() => setLimit(limit + PAGE)} sx={{ fontFamily: MONO, textTransform: 'none' }}>
                            Show {Math.min(PAGE, filtered.length - limit)} more
                        </Button>
                    </Box>
                )}
            </Section>
        </Box>
    );
};

export default IntelUsers;
